import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AuditPlanningPage from './pages/AuditPlanningPage';
import AuditExecutionPage from './pages/AuditExecutionPage';
import EvidencePage from './pages/EvidencePage';
import FindingsPage from './pages/FindingsPage';
import CAPAPage from './pages/CAPAPage';
import ReportsPage from './pages/ReportsPage';
import UserManagementPage from './pages/UserManagementPage';
import DepartmentsPage from './pages/DepartmentsPage';
import ISOControlsPage from './pages/ISOControlsPage';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="h-8 w-8 border-4 border-[#4A5690] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// App Content with Routes
const AppContent = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/audits"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Auditor']}>
            <AuditPlanningPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-execution"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Auditor', 'SPOC']}>
            <AuditExecutionPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/evidence"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Auditor', 'SPOC']}>
            <EvidencePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/findings"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Auditor', 'SPOC']}>
            <FindingsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/capa"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Auditor', 'SPOC']}>
            <CAPAPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['Admin', 'Auditor', 'Management']}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/departments"
        element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <DepartmentsPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/controls"
        element={
          <ProtectedRoute allowedRoles={['Admin']}>
            <ISOControlsPage />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
